/* eslint-disable react-refresh/only-export-components */
import {
  EditableButtons,
  EditableDataProvider,
  useEditableData,
} from "@/components/custom/editable-data";
import { ScheduledJobBase } from "./-job-definitions-context";
import { useCurrentRef } from "@/hooks/use-current-ref";
import React from "react";
import { functionalUpdate } from "@tanstack/react-table";
import { asNumber, cn, isDefined, setForm } from "@/lib/utils";
import SelectInput, {
  OptionType,
} from "@/components/select-input/select-input";
import CronInput from "@/components/custom/cron-input";
import { useScheduledJob } from "./-scheduled-job-context";
import { FormDatePickerItem } from "@/components/custom/form-inputs/date-picker";

import {
  FormItemCommonProps,
  FormItemContent,
} from "@/components/custom/form-inputs/common";
import { FormTzPickerItem } from "@/components/custom/form-inputs/tz-picker";
import { useTranslation } from "react-i18next";
import { t, TFunction } from "i18next";
import { useTimeSettings } from "@/state/time-settings";
import _ from "lodash";
import TimeSelector from "@/components/custom/time-selector";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  EVERY_UNIT_MS,
  ScheduleStartAll,
  ScheduleStartExisting,
  scheduleStartOf,
  scheduleTransition,
  type EveryUnit,
  type ScheduleStart,
} from "./-schedule-mode";

// Re-exported so existing importers keep working; the logic lives in -schedule-mode.ts.
export { EVERY_UNIT_MS, scheduleStartOf };
export type { EveryUnit, ScheduleStart } from "./-schedule-mode";

export type ScheduleData = Partial<
  Pick<
    ScheduledJobBase,
    | "every"
    | "duration"
    | "timezone"
    | "cronStart"
    | "cronEnd"
    | "startDate"
    | "endDate"
  > & { runNow?: boolean }
>;

// const ScheduleStart = [
//   {
//     schedule: true,
//     repeat: "cron",
//     runNow: false as false | undefined,
//     label: "recurring",
//   } as const,
//   {
//     schedule: true,
//     repeat: "interval",
//     runNow: false as false | undefined,
//     label: "every",
//   } as const,
//   {
//     schedule: true,
//     repeat: "once",
//     runNow: false as false | undefined,
//     label: "once",
//   } as const,
//   {
//     schedule: false,
//     repeat: undefined,
//     runNow: false as false | undefined,
//     label: "draft",
//   } as const,
//   {
//     schedule: false,
//     repeat: undefined,
//     runNow: true,
//     label: "runNow",
//   } as const,
// ] as const;

// type ScheduleStart = (typeof ScheduleStart)[number];

type ScheduleStartState = {
  schedule: ScheduleStart;
  setSchedule: React.Dispatch<React.SetStateAction<ScheduleStart>>;
};

export type EditableDataScheduleStartProps = {
  isNullable?: boolean;
};

function useEditableDataScheduleStartState({
  isNullable,
}: EditableDataScheduleStartProps) {
  const { data, form, editMode } = useEditableData<ScheduleData>();
  const dataRef = useCurrentRef(data);
  const runNow = form.watch("runNow");
  const schedule__: ScheduleStart = scheduleStartOf({ ...data, runNow });
  const [schedule_, setSchedule_] = React.useState(schedule__);
  React.useEffect(() => setSchedule_(schedule__), [schedule__]);
  const schedule = editMode ? schedule_ : schedule__;
  const setSchedule = React.useCallback<typeof setSchedule_>(
    (a) =>
      setSchedule_((s) => {
        const next = functionalUpdate(a, s);
        if (next !== s && editMode) {
          setForm(
            form,
            scheduleTransition(
              next,
              dataRef?.current,
              !!isNullable,
            ) as unknown as ScheduleData,
          );
        }
        return next;
      }),
    [form, editMode, isNullable],
  );
  return { schedule, setSchedule };
}

export const ScheduleStartContext = React.createContext<ScheduleStartState>(
  undefined as unknown as ScheduleStartState,
);

export function ScheduleStartProvider({
  defaultValue,
  children,
}: {
  defaultValue: ScheduleStart;
  children?: React.ReactNode;
}) {
  const [schedule, setSchedule] = React.useState<ScheduleStart>(defaultValue);
  return (
    <ScheduleStartContext.Provider
      value={{ schedule, setSchedule }}
      children={children}
    />
  );
}

export function EditableDataScheduleStartProvider({
  children,
  ...props
}: {
  children?: React.ReactNode;
} & EditableDataScheduleStartProps) {
  return (
    <ScheduleStartContext.Provider
      value={useEditableDataScheduleStartState(props)}
      children={children}
    />
  );
}

export function useScheduleStart() {
  return React.useContext(ScheduleStartContext);
}

// const asOption = (v: ScheduleStart, t: TFunction) =>
//   ({
//     value: v.label,
//     label: t(v.label),
//     data: v,
//   }) satisfies OptionType<string> & { data: ScheduleStart };

const asOption = (value: ScheduleStart, t: TFunction) =>
  ({
    value,
    label: t(value),
  }) satisfies OptionType<ScheduleStart>;

/**
 * ScheduleStartToggle - Allows selecting schedule type (recurring, once, draft, runNow)
 *
 * @param allowedScheduleTypes - If undefined/null, all schedule types are available (based on omitRunNow).
 *                                If provided, only the specified types will be shown.
 *                                If only one type is allowed, the selector is hidden (non-editable).
 */
function ScheduleStartToggle({
  omitRunNow,
  allowedScheduleTypes,
  ...props
}: Omit<
  Parameters<typeof SelectInput>[0],
  "isMulti" | "isClearable" | "onChange" | "value" | "options"
> & { omitRunNow?: boolean; allowedScheduleTypes?: readonly ScheduleStart[] }) {
  const { editMode } = useEditableData<ScheduleData>();
  const { schedule, setSchedule } = useScheduleStart();
  const { t } = useTranslation();

  const availableOptions = React.useMemo(() => {
    if (allowedScheduleTypes) {
      return omitRunNow
        ? allowedScheduleTypes.filter((option) => option !== "runNow")
        : allowedScheduleTypes;
    }
    return omitRunNow ? ScheduleStartExisting : ScheduleStartAll;
  }, [omitRunNow, allowedScheduleTypes]);

  // Hide selector if there's only one option (non-editable)
  if (availableOptions.length <= 1) {
    return null;
  }

  return (
    editMode && (
      <SelectInput
        {...(props as {})}
        className={cn("w-full", props.className)}
        isMulti={false}
        isClearable={false}
        options={availableOptions.map((v) => asOption(v, t))}
        value={asOption(schedule, t)}
        onChange={(op) => setSchedule(op!.value)}
      />
    )
  );
}

function CronEndPicker({
  value,
  onChange,
  defaultValueCreator = () => "0 9 * * *",
}: {
  disabled?: boolean;
  className?: string;
  value?: string | null;
  onChange: (value?: string | null) => void;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  defaultValueCreator?: () => string;
}) {
  const [minute_ = "", hour_ = "", ...rest] = (value || "").split(" ");
  const minute = minute_ !== "" ? asNumber(minute_) : undefined;
  const hour = hour_ !== "" ? asNumber(hour_) : undefined;
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-2">
      <Checkbox
        checked={isDefined(value)}
        onCheckedChange={(next) => {
          onChange(next ? defaultValueCreator() : null);
        }}
      />
      <span>{t("stopOn")}</span>
      <TimeSelector
        disabled={!isDefined(value)}
        value={[hour, minute].join(" ")}
        onChange={(v) => {
          if (!isDefined(v)) return;
          const [hour, minute] = v.split(/[:\s]/);
          onChange(`${minute || "0"} ${hour} ${rest.join(" ")}`);
        }}
      />
    </div>
  );
}

export function EveryInput({ className }: { className?: string }) {
  const { form, editMode } = useEditableData<ScheduleData>();
  const { t } = useTranslation();
  const everyMs = form.watch("every");
  const [unit, setUnit] = React.useState<EveryUnit>(() =>
    everyMs && everyMs % EVERY_UNIT_MS.hours === 0 ? "hours" : "minutes",
  );
  const value = everyMs ? Math.round(everyMs / EVERY_UNIT_MS[unit]) : "";

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <span className="text-sm whitespace-nowrap">{t("every")}</span>
      <Input
        type="number"
        min={1}
        disabled={!editMode}
        value={value}
        onChange={(ev) => {
          const n = asNumber(ev.target.value);
          setForm(form, {
            every: isDefined(n) && n > 0 ? n * EVERY_UNIT_MS[unit] : undefined,
          });
        }}
        className="w-20"
      />
      <SelectInput
        isMulti={false}
        isClearable={false}
        isDisabled={!editMode}
        options={(Object.keys(EVERY_UNIT_MS) as EveryUnit[]).map((u) => ({
          value: u,
          label: t(u),
        }))}
        value={{ value: unit, label: t(unit) }}
        onChange={(op) => {
          const nextUnit = op!.value as EveryUnit;
          if (everyMs) {
            const current = Math.round(everyMs / EVERY_UNIT_MS[unit]);
            setForm(form, { every: current * EVERY_UNIT_MS[nextUnit] });
          }
          setUnit(nextUnit);
        }}
        className="w-32"
      />
    </div>
  );
}

function Schedule({
  label: _label,
  container,
  className,
}: FormItemCommonProps & { className?: string }) {
  const { data, form, editMode } = useEditableData<ScheduleData>();
  const { schedule } = useScheduleStart();
  const cronStart = form.watch("cronStart") ?? data?.cronStart;
  const cronEnd = form.watch("cronEnd") ?? data?.cronEnd;
  return (
    <FormItemContent
      {...container}
      label={editMode ? undefined : t(schedule ?? "schedule")}
      clear={null}
    >
      <div className={cn("flex flex-col gap-2", className)}>
        {schedule === "recurring" && (
          <>
            <CronInput
              disabled={!editMode}
              onChange={(cronStart) =>
                setForm(form, { every: undefined, cronStart })
              }
              value={cronStart}
            />
            <CronEndPicker
              value={cronEnd}
              onChange={(cronEnd) => setForm(form, { cronEnd: cronEnd! })}
            />
          </>
        )}
        {schedule === "interval" && <EveryInput />}
      </div>
    </FormItemContent>
  );
}

function StartDate(
  props: Omit<
    Parameters<typeof FormDatePickerItem>[0],
    "form" | "name" | "isDisabled" | "label"
  >,
) {
  const { form, editMode } = useEditableData<ScheduleData>();
  const { t } = useTranslation();
  const { as } = useTimeSettings();
  const timezone = form.watch("timezone");
  const timeSettings = React.useMemo(() => as(timezone), [as, timezone]);
  return (
    <FormDatePickerItem
      inline={true}
      {...props}
      container={{
        ...props.container,
        clear: props.container?.clear ?? (editMode ? undefined : null),
        clearProps: {
          resetTarget: null,
          isResettable: (v) => v !== null && v !== undefined,
        },
        inline: false,
      }}
      form={form}
      name="startDate"
      label={t("startDate")}
      placeholder={editMode ? "Pick a Date" : "<N/A>"}
      isDisabled={!editMode}
      {...timeSettings}
    />
  );
}

function EndDate(
  props: Omit<
    Parameters<typeof FormDatePickerItem>[0],
    "form" | "name" | "isDisabled" | "label"
  >,
) {
  const { form, editMode } = useEditableData<ScheduleData>();
  const { t } = useTranslation();
  const { as } = useTimeSettings();
  const timezone = form.watch("timezone");
  const timeSettings = React.useMemo(() => as(timezone), [as, timezone]);

  return (
    <FormDatePickerItem
      inline={true}
      {...props}
      container={{
        ...props.container,
        clear: props.container?.clear ?? (editMode ? undefined : null),
        clearProps: {
          resetTarget: null,
          isResettable: (v) => v !== null && v !== undefined,
        },
        inline: false,
      }}
      form={form}
      name="endDate"
      label={t("endDate")}
      placeholder={editMode ? "Pick a Date" : "<N/A>"}
      isDisabled={!editMode}
      {...timeSettings}
    />
  );
}

function TimeZone(
  props: Omit<
    Parameters<typeof FormTzPickerItem>[0],
    "form" | "name" | "isDisabled" | "label"
  >,
) {
  const { t } = useTranslation();
  const { form, editMode } = useEditableData<ScheduleData>();
  const { timeZone } = useTimeSettings();
  return (
    <FormTzPickerItem
      {...props}
      container={{
        ...props.container,
        clear: props.container?.clear ?? (editMode ? undefined : null),
        clearProps: {
          resetTarget: timeZone,
          isResettable: (v) => v !== timeZone,
        },
      }}
      form={form}
      name="timezone"
      isDisabled={!editMode}
      label={t("timezone")}
    />
  );
}

export function ScheduledJobsScheduleOnce() {
  return (
    <div className="flex flex-col items-start gap-2 w-full">
      <StartDate className="min-w-64 w-full" />
      <TimeZone className="min-w-64 w-full" />
    </div>
  );
}

export function ScheduledJobsScheduleContent({
  showEndDate = true,
}: {
  showEndDate?: boolean;
}) {
  const { schedule } = useScheduleStart();

  return (
    <div className="flex flex-col items-start gap-2 w-full">
      <Schedule className="min-w-64 w-full" />
      {schedule !== "runNow" && schedule !== "draft" && (
        <>
          <StartDate className="min-w-64 w-full" />
          {showEndDate && <EndDate className="min-w-64 w-full" />}
          <TimeZone className="min-w-64 w-full" />
        </>
      )}
    </div>
  );
}

export function ScheduledJobsSchedule({
  allowedScheduleTypes,
  showEndDate = true,
  editable = true,
  mapSubmit,
}: {
  allowedScheduleTypes?: readonly ScheduleStart[];
  showEndDate?: boolean;
  /** False hides the edit controls for a user without the job type's edit permission. */
  editable?: boolean;
  /**
   * Extra fields to merge into the update body, derived from the schedule values being saved.
   * A job type whose payload depends on the schedule mode (capacity tagging's run window) uses
   * this to migrate that payload in the same request that changes the mode.
   */
  mapSubmit?: (values: ScheduleData) => Record<string, unknown>;
} = {}) {
  const {
    scheduledJobId,
    scheduledJobResult: { data: scheduledJob },
    edit,
  } = useScheduledJob();

  return (
    <EditableDataProvider<ScheduleData>
      data={React.useMemo(
        () =>
          _.pick(scheduledJob! ?? {}, [
            "every",
            "duration",
            "timezone",
            "cronStart",
            "cronEnd",
            "startDate",
            "endDate",
          ]),
        [scheduledJob],
      )}
      editable={editable}
      id={scheduledJobId}
      isDefault={true}
      loading={edit.isPending}
      onSubmit={async (data) => {
        // async so a refusing mapSubmit rejects the save instead of throwing out of the handler
        const extra = mapSubmit?.(data) ?? {};
        return await edit.mutateAsync({
          requestBody: { ..._.omit(data, "runNow"), ...extra },
        });
      }}
    >
      <EditableDataScheduleStartProvider isNullable={true}>
        <div className="w-full relative">
          <div className="absolute top-0 left-0 w-full flex items-center gap-2">
            <div className="ml-auto flex items-center gap-2">
              <EditableButtons />
            </div>
          </div>
          <ScheduleStartToggle
            omitRunNow={true}
            allowedScheduleTypes={allowedScheduleTypes}
            className="pb-2 w-[calc(100%-7.5rem)]"
          />
          <ScheduledJobsScheduleContent showEndDate={showEndDate} />
        </div>
      </EditableDataScheduleStartProvider>
    </EditableDataProvider>
  );
}
